import {
  createSlice,
  createAsyncThunk,
  createSelector } from
"@reduxjs/toolkit";
import { ApolloClient, gql, NormalizedCacheObject } from "@apollo/client";
import { RootState } from "@src/redux/store";
import * as TYPES from "@src/redux/types";

const GRAPHQL = gql`
  query getChildOrganizationList(
    $filter: childOrganizationFilterInput!
    $pagination: paginationInput!
    $sort: [sortInput]
  ) {
    getChildOrganizationList(
      filter: $filter
      pagination: $pagination
      sort: $sort
    ) {
      items {
        level {
          id
          name
          allowKitAssign
          levelAttributeList{
            id
            name
            sortOrder
            fieldType
            optionList
          }
          childLevelList {
            id
            name
            allowKitAssign
               levelAttributeList{
            id
            name
            sortOrder
            fieldType
            optionList
          }
          }
        }
        childOrganizationList {
          id
          name
          parentOrganizationId
          level {
            id
            name
          }
        }
        levelAttributeValueList {
          id
          levelAttributeId
          value
        }
        isDeleted
        isActivated
        isArchived
        thirdPopulation
        femalePopulation
        malePopulation
        mobile
        email
        parentOrganizationId
        levelId
        groupId
        type
        usernamePrefix
        shortName
        name
        id
      }
      pagination {
        pageLimit
        pageNumber
        totalCount
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.OrganizationListState = {
  result: null,
  error: null,
  loading: false
};

export const getChildOrganizationListRequest = createAsyncThunk<
  TYPES.OrganizationList,
  {
    filter: TYPES.ChildOrganizationFilterInput;
    pagination: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  "organization/get_child_organization_list",
  async (
  params: {
    filter: TYPES.ChildOrganizationFilterInput;
    pagination: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params,
        fetchPolicy: "network-only"
      });
      return response.data.getChildOrganizationList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || "An error occurred",
        statusCode: err.statusCode || "500"
      });
    }
  }
);

const getChildOrganizationListSlice = createSlice({
  name: "get_child_organization_list",
  initialState: initialState,
  reducers: {
    getChildOrganizationListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getChildOrganizationListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(
      getChildOrganizationListRequest.fulfilled,
      (state, action) => {
        state.result = action.payload;
        state.loading = false;
      }
    ).
    addCase(
      getChildOrganizationListRequest.rejected,
      (state, action) => {
        state.error = action.payload as Error || {
          message: "Failed to fetch",
          statusCode: "500"
        };
        state.loading = false;
      }
    );
  }
});

export const { getChildOrganizationListClear } =
getChildOrganizationListSlice.actions;

export const getChildOrganizationList = createSelector(
  (state: RootState) => state.organization.getChildOrganizationList.result,
  (state: RootState) => state.organization.getChildOrganizationList.error,
  (state: RootState) => state.organization.getChildOrganizationList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getChildOrganizationListSlice.reducer;